import { createContext, useContext } from "react";
import {
  useActiveFile,
  useDirtyPaths,
  useOpenTabs,
  useWorkspaceFiles,
  useWorkspaceSelection,
  type WorkspaceSelection,
  type WorkspaceStore,
} from "./WorkspaceStore.js";
import type { Vfs } from "@archlang/viewer-core";

export const WorkspaceContext = createContext<WorkspaceStore | null>(null);

/** The composition's store. Throws if used outside a `WorkspaceProvider`. */
export function useWorkspace(): WorkspaceStore {
  const store = useContext(WorkspaceContext);
  if (!store) {
    throw new Error("useWorkspace must be used within a <WorkspaceProvider>");
  }
  return store;
}

/** The composition's store, or `null` outside a `WorkspaceProvider`. For
 *  surfaces that degrade gracefully (e.g. a header that only enables editing
 *  when a store is present). */
export function useOptionalWorkspace(): WorkspaceStore | null {
  return useContext(WorkspaceContext);
}

// Context-bound slice hooks — the ergonomic form primitives use.
export function useFiles(): Vfs {
  return useWorkspaceFiles(useWorkspace());
}
export function useActiveFilePath(): string | null {
  return useActiveFile(useWorkspace());
}
export function useSelection(): WorkspaceSelection {
  return useWorkspaceSelection(useWorkspace());
}
export function useDirty(): ReadonlySet<string> {
  return useDirtyPaths(useWorkspace());
}
export function useTabs(): readonly string[] {
  return useOpenTabs(useWorkspace());
}
